/**
 * ExperimentsPanel.jsx — A/B Test des Guides des Crédits ESS
 *
 * Permet au Super Admin de créer, suivre et arrêter des expériences A/B
 * comparant 2 guides sur un espace donné, avec mesure de conversion.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getToken } from '../services/tokenService';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Plus, Loader2, FlaskConical, Trophy, AlertTriangle, CheckCircle2, Activity, Square, Users, TrendingUp, Clock } from 'lucide-react';
import { toast } from 'sonner';

const SPACES = [
  { value: 'default', label: 'Par défaut' },
  { value: 'member',  label: 'Membre' },
  { value: 'cooper',  label: "COOPER'S" },
  { value: 'expert',  label: 'Expert' },
];

const SPACE_LABEL = Object.fromEntries(SPACES.map((s) => [s.value, s.label]));

const FONTS = {
  playfair: { fontFamily: '"Playfair Display", Georgia, serif' },
  outfit: { fontFamily: '"Outfit", system-ui, sans-serif' },
  body: { fontFamily: '"IBM Plex Sans", system-ui, sans-serif' },
};

const apiUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}${p}`;
const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
  'Content-Type': 'application/json',
});

const StatusBadge = ({ status }) => {
  if (status === 'running') return <Badge className="bg-emerald-600 text-white font-semibold"><Activity className="h-3 w-3 mr-1" />En cours</Badge>;
  if (status === 'completed') return <Badge className="bg-slate-700 text-white font-semibold"><CheckCircle2 className="h-3 w-3 mr-1" />Terminée</Badge>;
  if (status === 'stopped') return <Badge className="bg-amber-700 text-white font-semibold"><Square className="h-3 w-3 mr-1" />Arrêtée</Badge>;
  return <Badge variant="outline">{status}</Badge>;
};

const ResultsView = ({ exp, onBack, onStop }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stopping, setStopping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(apiUrl(`/api/admin/experiments/${exp.id}/results`), { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setResults(await res.json());
    } catch (e) {
      toast.error(`Échec chargement résultats : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [exp.id]);

  useEffect(() => { load(); }, [load]);

  const handleStop = async () => {
    if (!window.confirm(`Arrêter manuellement l'expérience « ${exp.name} » ?`)) return;
    setStopping(true);
    try {
      const res = await fetch(apiUrl(`/api/admin/experiments/${exp.id}/stop`), { method: 'POST', headers: authHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success('Expérience arrêtée');
      onStop();
    } catch (e) {
      toast.error(`Échec arrêt : ${e.message}`);
    } finally {
      setStopping(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" />Chargement…</div>;
  }
  if (!results) return null;

  const a = results.variant_a;
  const b = results.variant_b;
  const winner = results.winner;
  const sig = results.significance;
  const totalUsers = (a.unique_users || 0) + (b.unique_users || 0);

  const VariantCard = ({ data, label, isWinner }) => {
    const colorBg = label === 'A' ? 'from-blue-600 to-blue-700' : 'from-purple-600 to-purple-700';
    return (
      <div className={`relative bg-white border-2 ${isWinner ? 'border-amber-400 shadow-lg shadow-amber-100' : 'border-slate-200'} rounded-xl overflow-hidden transition-all`}>
        {isWinner && (
          <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider border border-amber-200">
            <Trophy className="h-3 w-3" />Gagnant
          </div>
        )}
        <div className={`bg-gradient-to-br ${colorBg} text-white p-4`}>
          <div className="text-[10px] uppercase tracking-[0.3em] opacity-90" style={FONTS.outfit}>Variant {label}</div>
          <h4 className="font-bold text-lg mt-1 truncate" style={FONTS.outfit}>{data.guide_name}</h4>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500" style={FONTS.outfit}>Vues uniques</div>
            <div className="text-2xl font-bold text-slate-900 tabular-nums" style={FONTS.outfit}>{data.unique_users || 0}</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500" style={FONTS.outfit}>Achats</div>
            <div className="text-2xl font-bold text-slate-900 tabular-nums" style={FONTS.outfit}>{data.count || 0}</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500" style={FONTS.outfit}>Crédits</div>
            <div className="text-2xl font-bold text-amber-700 tabular-nums" style={FONTS.outfit}>{data.credits || 0}</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500" style={FONTS.outfit}>CA (€)</div>
            <div className="text-2xl font-bold text-emerald-700 tabular-nums" style={FONTS.outfit}>{(data.amount_eur || 0).toFixed(2)}</div>
          </div>
          <div className="col-span-2 rounded-lg bg-gradient-to-r from-slate-900 to-slate-800 text-white p-3">
            <div className="text-[10px] uppercase tracking-widest text-white/60" style={FONTS.outfit}>Taux de conversion</div>
            <div className="text-3xl font-bold tabular-nums" style={FONTS.outfit}>
              {(data.conversion_rate || 0).toFixed(2)}<span className="text-base text-white/70 ml-0.5">%</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Button variant="outline" size="sm" onClick={onBack} className="border-slate-300" data-testid="back-to-experiments-btn">
            ← Retour à la liste
          </Button>
          <h2 className="text-xl font-bold text-slate-900 mt-2" style={FONTS.outfit}>{exp.name}</h2>
          <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
            <span>Espace : <strong>{SPACE_LABEL[exp.space]}</strong></span>
            <span>·</span>
            <span>Durée : {exp.duration_days}j</span>
            <span>·</span>
            <StatusBadge status={exp.status} />
          </div>
        </div>
        {exp.status === 'running' && (
          <Button onClick={handleStop} disabled={stopping} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="stop-experiment-btn">
            {stopping ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Square className="h-4 w-4 mr-2" />}Arrêter
          </Button>
        )}
      </div>

      {/* Top KPI banner */}
      <Card className="p-5 bg-gradient-to-br from-[#0a1e3f] to-[#0B3C5D] text-white border-0">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60" style={FONTS.outfit}>Échantillon total</div>
            <div className="text-2xl font-bold tabular-nums flex items-center gap-1" style={FONTS.outfit}>
              <Users className="h-4 w-4 text-[#fbbf24]" />{totalUsers}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60" style={FONTS.outfit}>Achats totaux</div>
            <div className="text-2xl font-bold tabular-nums" style={FONTS.outfit}>{(a.count || 0) + (b.count || 0)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60" style={FONTS.outfit}>Écart conversion</div>
            <div className="text-2xl font-bold tabular-nums flex items-center gap-1" style={FONTS.outfit}>
              <TrendingUp className="h-4 w-4 text-[#fbbf24]" />{(results.delta_points || 0).toFixed(2)} pts
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60" style={FONTS.outfit}>Significativité</div>
            <div className="text-sm font-bold mt-1" style={FONTS.outfit}>
              {sig === 'significant' && <Badge className="bg-emerald-500 text-white font-semibold">Statistique</Badge>}
              {sig === 'robust' && <Badge className="bg-emerald-600 text-white font-semibold">Robuste</Badge>}
              {sig === 'no_winner' && <Badge className="bg-slate-500 text-white font-semibold">Pas de gagnant</Badge>}
              {sig === 'insufficient' && <Badge className="bg-amber-500 text-white font-semibold"><AlertTriangle className="h-3 w-3 mr-1" />Insuffisant</Badge>}
            </div>
          </div>
        </div>
      </Card>

      {/* Variant comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <VariantCard data={a} label="A" isWinner={winner === 'A'} />
        <VariantCard data={b} label="B" isWinner={winner === 'B'} />
      </div>

      {sig === 'insufficient' && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <p className="text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <span>L&apos;échantillon est encore trop faible pour conclure de manière significative (minimum 30 utilisateurs par variant recommandé). Laissez tourner l&apos;expérience plus longtemps.</span>
          </p>
        </Card>
      )}
    </div>
  );
};

const ExperimentsPanel = ({ library, onClose }) => {
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedExp, setSelectedExp] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // form
  const [name, setName] = useState('');
  const [space, setSpace] = useState('cooper');
  const [variantA, setVariantA] = useState('');
  const [variantB, setVariantB] = useState('');
  const [duration, setDuration] = useState(14);

  const fetchExperiments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/admin/experiments'), { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setExperiments(data.experiments || []);
    } catch (e) {
      toast.error(`Échec chargement : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchExperiments(); }, [fetchExperiments]);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Le nom est obligatoire'); return; }
    if (!variantA || !variantB) { toast.error('Choisissez les 2 variants'); return; }
    if (variantA === variantB) { toast.error('Les 2 variants doivent être différents'); return; }
    setCreating(true);
    try {
      const res = await fetch(apiUrl('/api/admin/experiments'), {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          name, space,
          variant_a_guide_id: variantA,
          variant_b_guide_id: variantB,
          duration_days: duration,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success('Expérience démarrée');
      setCreateOpen(false);
      setName(''); setVariantA(''); setVariantB(''); setDuration(14);
      fetchExperiments();
    } catch (e) {
      toast.error(`Échec création : ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const totalRunning = useMemo(() => experiments.filter((e) => e.status === 'running').length, [experiments]);

  if (selectedExp) {
    return <ResultsView exp={selectedExp} onBack={() => { setSelectedExp(null); fetchExperiments(); }} onStop={() => fetchExperiments()} />;
  }

  return (
    <div className="space-y-4" data-testid="experiments-panel">
      {/* Header bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#0a1e3f] flex items-center justify-center">
            <FlaskConical className="h-5 w-5 text-[#fbbf24]" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900" style={FONTS.outfit}>Expériences A/B</h3>
            <p className="text-xs text-slate-500">Comparez 2 guides sur un espace pour mesurer la conversion</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="border-emerald-300 text-emerald-700"><Activity className="h-3 w-3 mr-1" />{totalRunning} en cours</Badge>
          <Button onClick={onClose} variant="outline" size="sm" className="border-slate-300">
            Fermer
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#0a1e3f] hover:bg-[#0B3C5D] text-white" data-testid="create-experiment-btn">
            <Plus className="h-4 w-4 mr-1" />Nouvelle expérience
          </Button>
        </div>
      </div>

      {/* List */}
      {loading && <div className="flex justify-center py-12 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" />Chargement…</div>}

      {!loading && experiments.length === 0 && (
        <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl">
          <FlaskConical className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <h4 className="font-semibold text-slate-700" style={FONTS.outfit}>Aucune expérience pour l&apos;instant</h4>
          <p className="text-sm text-slate-500 mt-1 mb-4 max-w-md mx-auto">
            Lancez votre première expérience pour comparer 2 guides sur un même espace utilisateur et mesurer l&apos;impact sur la conversion.
          </p>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#0a1e3f] hover:bg-[#0B3C5D] text-white">
            <Plus className="h-4 w-4 mr-1" />Créer une expérience
          </Button>
        </div>
      )}

      {!loading && experiments.length > 0 && (
        <div className="space-y-2">
          {experiments.map((exp) => (
            <div
              key={exp.id}
              className="grid grid-cols-12 gap-3 items-center p-3 md:p-4 bg-white border border-slate-200 rounded-xl hover:shadow-md hover:-translate-y-px transition-all cursor-pointer"
              onClick={() => setSelectedExp(exp)}
              data-testid={`experiment-row-${exp.id}`}
            >
              <div className="col-span-12 md:col-span-4 min-w-0">
                <h4 className="font-semibold text-slate-900 truncate" style={FONTS.outfit}>{exp.name}</h4>
                <p className="text-xs text-slate-500 mt-0.5 truncate">
                  {exp.variant_a_guide_name} <span className="text-slate-300">vs</span> {exp.variant_b_guide_name}
                </p>
              </div>
              <div className="col-span-4 md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Espace</div>
                <Badge variant="outline" className="mt-1 bg-slate-100 text-slate-800 font-semibold">{SPACE_LABEL[exp.space]}</Badge>
              </div>
              <div className="col-span-4 md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Durée</div>
                <div className="text-sm font-medium text-slate-700 flex items-center gap-1"><Clock className="h-3 w-3" />{exp.duration_days}j</div>
              </div>
              <div className="col-span-4 md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Démarrée</div>
                <div className="text-xs text-slate-700">{new Date(exp.start_date).toLocaleDateString('fr-FR')}</div>
              </div>
              <div className="col-span-12 md:col-span-2 flex md:justify-end">
                <StatusBadge status={exp.status} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-experiment-dialog">
          <DialogHeader>
            <DialogTitle style={FONTS.outfit}>Nouvelle expérience A/B</DialogTitle>
            <DialogDescription>
              Comparez 2 guides différents sur un même espace utilisateur. Le trafic est réparti 50/50 de manière déterministe.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500" style={FONTS.outfit}>Nom de l&apos;expérience *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Test conversion Cooper Q3 2026" className="mt-1" data-testid="exp-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500" style={FONTS.outfit}>Espace cible *</Label>
              <Select value={space} onValueChange={setSpace}>
                <SelectTrigger className="mt-1" data-testid="exp-space-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SPACES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500" style={FONTS.outfit}>Variant A (témoin) *</Label>
                <Select value={variantA} onValueChange={setVariantA}>
                  <SelectTrigger className="mt-1" data-testid="exp-variant-a"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{library.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500" style={FONTS.outfit}>Variant B (challenger) *</Label>
                <Select value={variantB} onValueChange={setVariantB}>
                  <SelectTrigger className="mt-1" data-testid="exp-variant-b"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{library.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500" style={FONTS.outfit}>Durée — {duration} jours</Label>
              <Input
                type="range" min={1} max={60} value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value, 10))}
                className="mt-1 cursor-pointer"
                data-testid="exp-duration-slider"
              />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                <span>1j</span><span>14j (recommandé)</span><span>60j</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Annuler</Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !name.trim() || !variantA || !variantB || variantA === variantB}
              className="bg-[#0a1e3f] hover:bg-[#0B3C5D] text-white"
              data-testid="confirm-create-experiment"
            >
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FlaskConical className="h-4 w-4 mr-2" />}
              Démarrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExperimentsPanel;
